// Brain model identity, labels and embedding eligibility shared by model pickers.
import type { BrainModelOption } from './types';
import { modelSupportsOutput } from './modelCatalog';

/** Short auth-source chip for a brain model (mirrors the `source` the daemon derives from how the model is
 *  reachable). The ONE source for every model picker so the chip set never drifts between them. */
export const SOURCE_BADGE: Record<BrainModelOption['source'], string> = {
  oauth: 'OAuth',
  'api-key': 'API',
  router: 'Router',
};

/** The catalog an embedding route may offer, shared by the core role and plugin editor.
 * OAuth accounts must positively advertise embeddings; API-key endpoints keep unknown-kind models. */
export function embeddingModelCatalog(models: readonly BrainModelOption[]): BrainModelOption[] {
  return models.filter((m) => m.kind !== 'image' && (m.source !== 'oauth' || m.outputs?.includes('embeddings')) && modelSupportsOutput(m, 'embeddings'));
}

/**
 * The IDENTITY of a brain model in a picker or an allow-list: its composite spec, which carries the
 * program AND the provider AND the model — and which is also the value the daemon stores, so a pick
 * round-trips. Deliberately NOT the visible label: two providers can offer the same model name, and
 * keying rows by that name collapses them into one ambiguous row (and makes a saved value unmatchable).
 */
export function brainModelId(m: Pick<BrainModelOption, 'exec'>): string {
  return m.exec;
}

/**
 * The catalog model name to DISPLAY throughout the ordinary UI. Structured identities already carry the
 * model separately, while stored exec strings are resolved through the catalog, never by splitting `/`:
 * a model id may itself contain slashes (`deepseek/deepseek-v4-pro`). An unknown raw string stays raw so a
 * removed/stale pick remains visible without pretending its provider/model boundary is known.
 *
 * Provider identity remains available in grouped picker headers and badges. Keys, persistence, requests,
 * diagnostics and ambiguity checks continue to use the full structured identity or exec.
 */
type BrainModelDisplayIdentity = Pick<BrainModelOption, 'model'>
  & Partial<Pick<BrainModelOption, 'provider' | 'providerLabel' | 'exec'>>;

export function brainModelLabel(
  identity: string | BrainModelDisplayIdentity,
  models?: readonly BrainModelOption[],
): string {
  if (typeof identity !== 'string') return identity.model;
  return models?.find((m) => brainModelId(m) === identity)?.model ?? identity;
}

/**
 * A provider-qualified diagnostic label, composed from structured/catalog fields without splitting the
 * exec. Ordinary visible model labels use {@link brainModelLabel}; keep this for title text and surfaces
 * where the full identity is itself the subject.
 *
 * Prefers the operator's provider label (`Ollama`) and falls back to its config id.
 * The daemon strips PI's internal `elowen-<id>` namespace at the API boundary.
 */
export function brainModelQualifiedLabel(
  identity: Pick<BrainModelOption, 'provider' | 'model'> & Partial<Pick<BrainModelOption, 'providerLabel'>>,
): string {
  const provider = identity.providerLabel || identity.provider;
  return provider ? `${provider}/${identity.model}` : identity.model;
}
